import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faSync } from "@fortawesome/free-solid-svg-icons"
import CustomModal from "../../basics/interface/CustomModal"
import {
	creerCouverture,
	envoyerJustificatif,
	fetchReferencesCouverture,
	modifierCouverture,
} from "../../../store/actions/couverturesActions"
import { showToast } from "../../../store/actions/toastActions"
import { ui, useEtroit } from "./couverturesUi"
import { COLORS } from "../../../utilities/Constants"

// Fenêtre « + Couverture » / « Modifier… » (GE-60, spec §6.3, maquette
// #m-form) — sur une fiche ou une mission pas encore convertie.
// - Assistance : partenaires + « Autre… » (nom libre) ;
// - en sortie du N° de dossier : le dossier existe-t-il déjà ailleurs
//   (ordre / mission / couverture) ? → proposer de requalifier la mission ;
// - Créer : switch de confirmation sur le bouton (règle UI V2, bouton déjà
//   dans un modal).
const PERIMETRES = ["Totalité", "Dépannage", "Transport", "Gardiennage"]
const AUTRE = "autre"

const etatInitial = (c) => {
	if (!c)
		return {
			assistance: "",
			libre: "",
			ref_dossier: "",
			ref_ordre: "",
			plafond: "",
			// Décision Jordan (3e revue, point 3) : plus de périmètre par
			// défaut — choix OBLIGATOIRE (Totalité = proposition, Dépannage /
			// Transport = confirmation automatique de l'ordre du même rôle).
			perimetre: "",
			precision: "",
			source: "telephone",
			interlocuteur: "",
			facturation: "",
		}
	const rapide = PERIMETRES.find(
		(p) => c.perimetre === p || (c.perimetre || "").startsWith(`${p} · `),
	)
	return {
		assistance: c.assistance_entite_id
			? String(c.assistance_entite_id)
			: c.assistance_libre
				? AUTRE
				: "",
		libre: c.assistance_libre || "",
		ref_dossier: c.ref_dossier || "",
		ref_ordre: c.ref_ordre || "",
		plafond: c.plafond === null ? "" : String(c.plafond),
		perimetre: rapide || "",
		precision: rapide
			? (c.perimetre || "").slice(rapide.length + 3)
			: c.perimetre || "",
		source:
			c.source === "manuel" || c.source === "telephone"
				? c.source
				: "telephone",
		interlocuteur: c.interlocuteur || "",
		facturation: c.facturation || "",
	}
}

// Champ du formulaire (hors composant : pas de perte de focus à la frappe).
const Champ = ({ label, full = false, children }) => (
	<div style={full ? { gridColumn: "1 / -1" } : null}>
		<label style={ui.fieldLabel}>{label}</label>
		{children}
	</div>
)

const CouvertureFormModal = ({
	// { depannage_id } | { dep_mission_id }
	rattachement,
	sousTitre,
	couverture = null,
	onClose,
	onSaved,
	// (missionId) → ouvre la requalification de la mission trouvée.
	onRequalifier = null,
}) => {
	const dispatch = useDispatch()
	const etroit = useEtroit()
	const assistances = useSelector((state) => state.autreInfo.assistances)
	const [f, setF] = useState(() => etatInitial(couverture))
	const [fichier, setFichier] = useState(null)
	const [refsExistantes, setRefsExistantes] = useState([])
	const [confirmer, setConfirmer] = useState(false)
	const [etat, setEtat] = useState({ saving: false, saved: false })
	const [erreur, setErreur] = useState(null)
	const premierChamp = useRef(null)
	const edition = !!couverture
	// Source choisie au bureau seulement (e-mail / import : figée).
	const sourceEditable =
		!edition || ["telephone", "manuel"].includes(couverture.source)

	useEffect(() => {
		if (premierChamp.current) premierChamp.current.focus()
	}, [])

	const set = (cle) => (e) => {
		setConfirmer(false)
		const valeur = e && e.target ? e.target.value : e
		setF((prec) => ({ ...prec, [cle]: valeur }))
	}

	const verifierRef = () => {
		const ref = f.ref_dossier.trim()
		if (ref.length < 4) {
			setRefsExistantes([])
			return
		}
		dispatch(
			fetchReferencesCouverture(ref, {
				fiche: rattachement.depannage_id,
				mission: rattachement.dep_mission_id,
			}),
		).then((liste) => setRefsExistantes(liste || []))
	}

	const valide =
		((f.assistance && f.assistance !== AUTRE) ||
			(f.assistance === AUTRE && f.libre.trim() !== "")) &&
		PERIMETRES.includes(f.perimetre)

	const payload = () => {
		const perimetre = [f.perimetre, f.precision.trim()]
			.filter(Boolean)
			.join(" · ")
		return {
			...rattachement,
			assistance_entite_id:
				f.assistance && f.assistance !== AUTRE
					? parseInt(f.assistance)
					: null,
			assistance_libre: f.assistance === AUTRE ? f.libre.trim() : "",
			ref_dossier: f.ref_dossier.trim(),
			ref_ordre: f.ref_ordre.trim(),
			plafond: f.plafond.trim(),
			perimetre,
			...(sourceEditable ? { source: f.source } : {}),
			interlocuteur: f.interlocuteur.trim(),
			facturation: f.facturation.trim(),
		}
	}

	const enregistrer = () => {
		if (!valide || etat.saving || etat.saved) return
		if (!confirmer) {
			setConfirmer(true)
			return
		}
		setErreur(null)
		setEtat({ saving: true, saved: false })
		const action = edition
			? modifierCouverture(couverture.id, payload())
			: creerCouverture(payload())
		dispatch(action).then(async (res) => {
			if (!res || res.erreur) {
				setErreur(res && res.erreur)
				setEtat({ saving: false, saved: false })
				setConfirmer(false)
				return
			}
			let final = res
			const id = edition ? couverture.id : res.id
			if (fichier && id) {
				const r2 = await dispatch(envoyerJustificatif(id, fichier))
				// Couverture enregistrée même si l'envoi du fichier échoue :
				// on le signale sans rouvrir la saisie (pas de doublon).
				if (r2 && r2.erreur)
					dispatch(
						showToast(
							"warning",
							`Couverture enregistrée, justificatif non envoyé : ${
								r2.erreur.message || "erreur"
							}`,
						),
					)
				else if (r2) final = r2
			}
			setEtat({ saving: false, saved: true })
			setTimeout(() => onSaved(final), 600)
		})
	}

	const titreBouton = etat.saving ? (
		<FontAwesomeIcon icon={faSync} spin />
	) : etat.saved ? (
		<FontAwesomeIcon icon={faCheck} />
	) : confirmer ? (
		"Confirmer ?"
	) : edition ? (
		"Enregistrer"
	) : (
		"Créer la couverture"
	)

	const trouve = refsExistantes.find(
		(r) => r.type === "ordre" || r.type === "mission",
	)
	const autreCouv = refsExistantes.find((r) => r.type === "couverture")

	return (
		<CustomModal
			title={edition ? "Modifier la couverture" : "Nouvelle couverture"}
			subtitle={sousTitre}
			onClose={etat.saving ? () => {} : onClose}
			showCloseButton={!etat.saving}
			maxWidth={760}
			buttons={[
				{
					title: "Annuler",
					styleName: "secondary",
					onClick: onClose,
					disabled: etat.saving,
				},
				{
					title: titreBouton,
					styleName: "primary",
					onClick: enregistrer,
					disabled: !valide || etat.saving || etat.saved,
				},
			]}
		>
			<div style={ui.form(etroit ? 1 : 2)}>
				<Champ label="Assistance">
					<select
						ref={premierChamp}
						style={ui.input}
						value={f.assistance}
						onChange={set("assistance")}
					>
						<option value="">Choisir…</option>
						{(assistances || []).map((a) => (
							<option key={`as_${a.id}`} value={a.id}>
								{a.label}
							</option>
						))}
						<option value={AUTRE}>Autre…</option>
					</select>
					{f.assistance === AUTRE && (
						<input
							style={{ ...ui.input, marginTop: 6 }}
							placeholder="Nom de l'assistance"
							value={f.libre}
							onChange={set("libre")}
							maxLength={100}
						/>
					)}
				</Champ>
				<Champ label="N° de dossier">
					<input
						style={ui.input}
						placeholder="ex. F1479610"
						value={f.ref_dossier}
						onChange={set("ref_dossier")}
						onBlur={verifierRef}
						maxLength={50}
					/>
				</Champ>
				{(trouve || autreCouv) && (
					<div style={{ gridColumn: "1 / -1" }}>
						<div style={ui.hint}>
							{trouve ? (
								<>
									ℹ Le dossier <b>{f.ref_dossier.trim()}</b>{" "}
									existe déjà :{" "}
									{trouve.type === "ordre"
										? `ordre ${trouve.assistance} rattaché à `
										: ""}
									{trouve.mission_id ? (
										<>
											la mission{" "}
											<b>M{trouve.mission_id}</b>
											{trouve.recu_le
												? ` (importée le ${trouve.recu_le.slice(0, 5)})`
												: ""}
											.{" "}
										</>
									) : (
										"sans mission. "
									)}
									{trouve.mission_id &&
										trouve.requalifiable &&
										onRequalifier && (
											<button
												type="button"
												style={ui.lien}
												onClick={() =>
													onRequalifier(
														trouve.mission_id,
													)
												}
											>
												Requalifier M{trouve.mission_id}{" "}
												en couverture de cette fiche
											</button>
										)}
								</>
							) : (
								<>
									ℹ Une couverture {autreCouv.assistance} de
									même référence existe déjà
									{autreCouv.fiche_id
										? ` sur la fiche ${autreCouv.fiche_id}`
										: autreCouv.mission_id
											? ` sur la mission M${autreCouv.mission_id}`
											: ""}
									.
								</>
							)}
						</div>
					</div>
				)}
				<Champ label="Réf. ordre (facultatif)">
					<input
						style={ui.input}
						placeholder="ex. S1567682"
						value={f.ref_ordre}
						onChange={set("ref_ordre")}
						maxLength={50}
					/>
				</Champ>
				<Champ label="Plafond TTC (facultatif)">
					<input
						style={ui.input}
						placeholder="ex. 540.00"
						inputMode="decimal"
						value={f.plafond}
						onChange={set("plafond")}
					/>
				</Champ>
				<Champ label="Périmètre" full>
					<div style={{ ...ui.pills, marginBottom: 6 }}>
						{PERIMETRES.map((p) => (
							<button
								key={`per_${p}`}
								type="button"
								style={ui.pill(f.perimetre === p)}
								onClick={() =>
									set("perimetre")(f.perimetre === p ? "" : p)
								}
							>
								{p}
							</button>
						))}
					</div>
					{!PERIMETRES.includes(f.perimetre) && (
						<div
							style={{
								fontSize: 12,
								color: COLORS.warning,
								marginBottom: 6,
							}}
						>
							Choisir le périmètre (obligatoire).
						</div>
					)}
					<input
						style={ui.input}
						placeholder="Précision (facultatif)"
						value={f.precision}
						onChange={set("precision")}
						maxLength={80}
					/>
				</Champ>
				{sourceEditable ? (
					<Champ label="Source">
						<div style={ui.pills}>
							<button
								type="button"
								style={ui.pill(f.source === "telephone")}
								onClick={() => set("source")("telephone")}
							>
								{"☎ Téléphone"}
							</button>
							<button
								type="button"
								style={ui.pill(f.source === "manuel")}
								onClick={() => set("source")("manuel")}
							>
								{"✍ Autre"}
							</button>
						</div>
					</Champ>
				) : (
					<Champ label="Source">
						<div style={{ fontSize: 13, paddingTop: 6 }}>
							{couverture.source_label}
						</div>
					</Champ>
				)}
				<Champ label="Interlocuteur">
					<input
						style={ui.input}
						placeholder="ex. M. Rochat"
						value={f.interlocuteur}
						onChange={set("interlocuteur")}
						maxLength={100}
					/>
				</Champ>
				<Champ label="Consignes de facturation" full>
					<textarea
						style={{
							...ui.input,
							resize: "vertical",
							minHeight: 54,
						}}
						placeholder="ex. Facture au nom du client, à envoyer à…"
						value={f.facturation}
						onChange={set("facturation")}
					/>
				</Champ>
				<Champ label="Justificatif (facultatif)" full>
					<input
						type="file"
						style={{ ...ui.input, padding: 6 }}
						accept=".pdf,.png,.jpg,.jpeg,.eml,.msg,.txt"
						onChange={(e) =>
							setFichier(
								e.target.files && e.target.files[0]
									? e.target.files[0]
									: null,
							)
						}
					/>
					{edition && couverture.justificatif_nom && !fichier && (
						<div style={{ fontSize: 12, marginTop: 4 }}>
							Actuel : {couverture.justificatif_nom}
						</div>
					)}
				</Champ>
			</div>
			{erreur && (
				<div style={ui.erreur}>
					{erreur.message || "L'enregistrement a échoué."}
				</div>
			)}
		</CustomModal>
	)
}

export default CouvertureFormModal
